import React, { useState, useEffect } from 'react';
import api from '../services/api';
import UserViewPreferences from './UserViewPreferences';
import PinModal from './PinModal';

const MobileCommands = ({ user, commandPreferences = {}, viewPreferences = {}, onViewPreferencesChange }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executingCommand, setExecutingCommand] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [commandHistory, setCommandHistory] = useState([]);

  // Modo oscuro - sincronizado con MobileDashboard
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('mobileDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Modal de preferencias de visualización
  const [showPreferences, setShowPreferences] = useState(false);
  // PIN modal
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('mobileDarkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [devicesRes] = await Promise.all([
        api.get('/api/devices')
      ]);

      const devicesData = devicesRes.data || [];

      // Filtrar solo dispositivos que tienen comandos visibles en dashboard
      const devicesWithCommands = devicesData.filter(device =>
        device.commands && device.commands.some(cmd => cmd.showInDashboard !== false)
      );

      setDevices(devicesWithCommands);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const executeCommand = async (device, command) => {
    const commandKey = `${device._id}-${command._id}`;

    if (executingCommand === commandKey) return; // Prevenir doble ejecución

    // Mostrar confirmación si es requerida
    if (command.confirmRequired) {
      const confirmMessage = command.confirmMessage || `¿Estás seguro de ejecutar "${command.label}"?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    setExecutingCommand(commandKey);

    try {
      console.log(`🚀 Executing command ${command.label} on device ${device.name}`);

      const response = await api.sendCommand(device._id, command._id);

      if (response.data.success || response.status === 200) {
        // Agregar al historial local
        const newHistoryEntry = {
          id: Date.now(),
          deviceName: device.name,
          commandLabel: command.label,
          commandIcon: command.icon,
          timestamp: new Date(),
          success: true
        };

        setCommandHistory(prev => [newHistoryEntry, ...prev.slice(0, 9)]); // Mantener solo los últimos 10

        console.log(`✅ Command ${command.label} executed successfully`);
      } else {
        console.error('❌ Command execution failed:', response);
        alert(`Error al ejecutar comando: ${response.data?.message || 'Error desconocido'}`);
      }
    } catch (error) {
      console.error('❌ Error executing command:', error);
      alert(`Error al ejecutar comando: ${error.response?.data?.message || error.message}`);
    } finally {
      setExecutingCommand(null);
    }
  };

  const getDeviceStatus = (device) => {
    if (!device.lastSeen) return 'unknown';

    const lastSeen = new Date(device.lastSeen);
    const now = new Date();
    const minutesAgo = (now - lastSeen) / (1000 * 60);

    if (minutesAgo <= 2) return 'online';
    if (minutesAgo <= 10) return 'warning';
    return 'offline';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'offline': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getCommandButtonColor = (command) => {
    switch (command.buttonColor) {
      case 'danger': return '#dc2626';
      case 'warning': return '#d97706';
      case 'success': return '#059669';
      case 'info': return '#0284c7';
      default: return '#3b82f6';
    }
  };

  // Filtrar dispositivos según permisos del usuario
  const getVisibleDevices = () => {
    if (!Array.isArray(devices)) return [];

    // Si es admin, puede ver todos los dispositivos
    if (!user || user.role === 'admin') {
      return devices;
    }

    // Para otros usuarios, solo dispositivos que tengan permisos explícitos
    if (!user.devicePermissions) {
      return [];
    }

    return devices.filter(device => {
      const deviceId = device._id;
      const devicePermissions = user.devicePermissions[deviceId];
      return devicePermissions && devicePermissions.allowed;
    });
  };

  // Filtrar comandos de un dispositivo según permisos y preferencias
  const getFilteredCommands = (device) => {
    if (!device.commands) return [];

    // Filtrar primero por showInDashboard
    let visibleCommands = device.commands.filter(cmd => cmd.showInDashboard !== false);

    // Filtrar por permisos de dispositivo (admin ve todo)
    if (user && user.role !== 'admin') {
      if (user.devicePermissions && user.devicePermissions[device._id]) {
        const devicePermissions = user.devicePermissions[device._id];
        if (devicePermissions.commands && devicePermissions.commands.length > 0) {
          visibleCommands = visibleCommands.filter(cmd =>
            devicePermissions.commands.includes(cmd.name)
          );
        }
      }
    }

    // Aplicar preferencias de comandos del usuario
    const userCommandPrefs = commandPreferences[device._id];
    if (userCommandPrefs !== undefined && Array.isArray(userCommandPrefs)) {
      if (userCommandPrefs.length === 0) {
        return []; // Usuario ha deshabilitado todos los comandos
      } else {
        visibleCommands = visibleCommands.filter(cmd =>
          userCommandPrefs.includes(cmd.name)
        );
      }
    }

    return visibleCommands;
  };

  // Manejar click en boton de preferencias (verificar PIN si existe)
  const handlePreferencesClick = async () => {
    try {
      const response = await api.get('/api/users/me/has-pin');
      if (response.data.hasPin && !pinVerified) {
        setShowPinModal(true);
      } else {
        setShowPreferences(true);
      }
    } catch (error) {
      console.error('Error checking PIN:', error);
      // Si hay error, intentar abrir directamente
      setShowPreferences(true);
    }
  };

  const handlePinSuccess = () => {
    setShowPinModal(false);
    setPinVerified(true);
    setShowPreferences(true);
  };

  const filteredDevices = getVisibleDevices();

  if (loading) {
    return (
      <div className="mobile-container" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
            <span style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '3px solid var(--border-color)',
              borderTop: '3px solid var(--primary-color)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></span>
          </div>
          <div style={{ color: 'var(--text-secondary)' }}>Cargando comandos...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-container">
      {/* Header */}
      <div className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              background: 'var(--bg-secondary)',
              borderRadius: '12px',
              padding: '0.5rem',
              border: '1px solid var(--border-color)',
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src="/logo-designerlow.png"
                alt="Logo"
                style={{ width: '32px', height: '32px', objectFit: 'contain' }}
              />
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: '600',
                color: 'var(--text-primary)'
              }}>
                Panel de Control
              </h1>
              <p style={{
                margin: '0.125rem 0 0 0',
                color: 'var(--text-secondary)',
                fontSize: '0.8rem'
              }}>
                Comandos disponibles
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              className="theme-toggle"
              onClick={handlePreferencesClick}
              title="Preferencias de visualización"
              style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '0.5rem',
                cursor: 'pointer',
                fontSize: '1.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              👁️
            </button>
            <button
              className="theme-toggle"
              onClick={() => setDarkMode(!darkMode)}
              title={darkMode ? 'Modo claro' : 'Modo oscuro'}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          </div>
        </div>

        <div style={{
          marginTop: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          fontSize: '0.75rem',
          color: 'var(--text-secondary)'
        }}>
          <span>🕒</span>
          <span>Actualizado: {lastUpdate.toLocaleTimeString('es-AR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          })}</span>
        </div>
      </div>

      {/* Command History */}
      {commandHistory.length > 0 && (
        <div className="mobile-device-card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: '600', color: 'var(--text-primary)' }}>
            📋 Comandos recientes
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {commandHistory.slice(0, 3).map(entry => (
              <div key={entry.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.5rem',
                background: 'var(--bg-secondary)',
                borderRadius: '6px',
                fontSize: '0.8rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
                  <span>{entry.commandIcon || '🎮'}</span>
                  <span style={{ fontWeight: '500' }}>{entry.deviceName}</span>
                  <span style={{ color: 'var(--text-secondary)' }}>-</span>
                  <span>{entry.commandLabel}</span>
                </div>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>
                  {entry.timestamp.toLocaleTimeString('es-AR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Devices with Commands Grid */}
      <div className="mobile-devices-grid">
        {filteredDevices.map(device => {
          const status = getDeviceStatus(device);
          const statusColor = getStatusColor(status);

          // Colores según estado del dispositivo
          const isOnline = status === 'online';
          const deviceBorderColor = isOnline ? 'rgba(16, 185, 129, 0.6)' : 'rgba(239, 68, 68, 0.6)';
          const deviceBgColor = isOnline
            ? 'rgba(16, 185, 129, 0.08)'
            : 'rgba(239, 68, 68, 0.08)';

          return (
            <div key={device._id} className="mobile-device-card" style={{
              border: `2px solid ${deviceBorderColor}`,
              background: deviceBgColor,
              boxShadow: `0 0 8px ${isOnline ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)'}`
            }}>
              {/* Device Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.75rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '1.25rem' }}>🎮</span>
                  <h3 style={{
                    margin: 0,
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: 'var(--text-primary)'
                  }}>
                    {device.name}
                  </h3>
                </div>
                <span style={{
                  background: statusColor,
                  color: 'white',
                  padding: '0.2rem 0.5rem',
                  borderRadius: '12px',
                  fontSize: '0.7rem',
                  fontWeight: '500'
                }}>
                  {status === 'online' ? 'En linea' : status === 'warning' ? 'Intermitente' : 'Desconectado'}
                </span>
              </div>

              {/* Commands */}
              <div className="mobile-magnitudes-grid">
                {getFilteredCommands(device).map(command => {
                  const commandKey = `${device._id}-${command._id}`;
                  const isExecuting = executingCommand === commandKey;
                  const buttonColor = getCommandButtonColor(command);

                  return (
                    <button
                      key={command._id}
                      onClick={() => executeCommand(device, command)}
                      disabled={isExecuting || status === 'offline'}
                      style={{
                        background: isExecuting ? '#9ca3af' : buttonColor,
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '1rem 0.75rem',
                        cursor: isExecuting || status === 'offline' ? 'not-allowed' : 'pointer',
                        textAlign: 'center',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        opacity: status === 'offline' ? 0.5 : 1,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem' }}>
                        {isExecuting ? '⏳' : command.icon || '🎮'}
                      </div>
                      <div>
                        {isExecuting ? 'Ejecutando...' : command.label}
                      </div>
                      {command.confirmRequired && (
                        <div style={{ fontSize: '0.65rem', opacity: 0.8 }}>
                          Requiere confirmación
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {status === 'offline' && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.5rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '6px',
                  fontSize: '0.7rem',
                  color: 'var(--danger-color)',
                  textAlign: 'center'
                }}>
                  Dispositivo desconectado
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredDevices.length === 0 && (
        <div className="mobile-device-card" style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎮</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--text-secondary)' }}>
            Sin comandos
          </h3>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            No hay dispositivos con comandos disponibles.
          </p>
        </div>
      )}

      {/* Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        <button
          className="mobile-nav-item"
          onClick={() => window.location.href = '/mobile'}
        >
          <span className="nav-icon">📊</span>
          <span>Dashboard</span>
        </button>
        <button
          className="mobile-nav-item active"
          onClick={() => window.location.href = '/mobile-commands'}
        >
          <span className="nav-icon">⚡</span>
          <span>Comandos</span>
        </button>
        <button
          className="mobile-nav-item"
          onClick={() => window.location.href = '/mobile'}
        >
          <span className="nav-icon">🔕</span>
          <span>Alertas</span>
        </button>
        <button
          className="mobile-nav-item"
          onClick={() => window.location.href = '/mobile-logout'}
        >
          <span className="nav-icon">🚪</span>
          <span>Salir</span>
        </button>
      </nav>

      {/* Modal de PIN */}
      {showPinModal && (
        <PinModal
          isOpen={showPinModal}
          onClose={() => setShowPinModal(false)}
          onSuccess={handlePinSuccess}
          mode="verify"
        />
      )}

      {/* Modal de Preferencias de Visualización */}
      {showPreferences && (
        <UserViewPreferences
          isOpen={showPreferences}
          onClose={() => setShowPreferences(false)}
          onSave={onViewPreferencesChange}
        />
      )}
    </div>
  );
};

export default MobileCommands;